import { t } from "i18next";
import React, { useContext, useEffect, useState, useRef } from "react";
import { Modal, Table, Button, Form } from "react-bootstrap";
import { getPlanning, exportWeeklyPlanningPDF, createPlanningWeekContent, updatePlanningWeekContent } from "../../../../services/planning";
import { Planning, PlanningWeek, PlanningDay, PlanningDayValue, PlanningMonth, planningLevels, emptyPlanning, PlanningWeekContent } from "../../../../interfaces/planning";
import { LoadingSpinner } from "../../../general/loading-spinner";
import ReactECharts from 'echarts-for-react';
import { UserContext } from "../../../../App";

interface WeeklyPlanningModalProps {
    show: boolean;
    seasonId: number;
    teamId: number;
    weekKey: string;
    onClose: () => void;
}

/**
 * Helper function to parse numbers with Spanish decimal format (comma)
 */
const parseSpanishFloat = (value: string): number => {
    if (!value) return 0;
    const normalizedValue = value.replace(',', '.');
    return parseFloat(normalizedValue) || 0;
};

function WeeklyPlanningModal({ show, seasonId, teamId, weekKey, onClose }: WeeklyPlanningModalProps) {
    const { userProfile } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [planning, setPlanning] = useState<Planning>(emptyPlanning);
    const [selectedWeek, setSelectedWeek] = useState<PlanningWeek | null>(null);
    const [currentWeekKey, setCurrentWeekKey] = useState<string>('');
    const [exportingPDF, setExportingPDF] = useState(false);
    const weekSelectorRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<any>(null);

    // Inline editing states for Content Distribution
    const [editingContent, setEditingContent] = useState<{
        fieldKey: string;
        fieldName: string;
        value: string;
        color: string;
        observations: string;
    } | null>(null);
    const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
    const [savingContent, setSavingContent] = useState(false);

    // Check if role array contains an object with 'athlete' in the id field
    const isAthlete = userProfile.role.some((r: any) => {
        if (typeof r === 'string') return r.includes('athlete');
        if (typeof r === 'object' && r.id) return r.id.includes('athlete');
        return false;
    });

    useEffect(() => {
        if (show && seasonId && teamId && weekKey) {
            setLoading(true);
            getPlanning(seasonId, teamId).then((response: any) => {
                const planningData = response.data;
                setPlanning(planningData);

                // Find the specified week
                let weekToSelect: PlanningWeek | null = null;
                for (const month of planningData.months) {
                    const targetWeek = month.weeks.find((w: PlanningWeek) => w.key === weekKey);
                    if (targetWeek) {
                        weekToSelect = targetWeek;
                        break;
                    }
                }

                setSelectedWeek(weekToSelect);
                setLoading(false);

                // Scroll to the selected week in the carousel
                setTimeout(() => {
                    if (weekToSelect && weekSelectorRef.current) {
                        const weekElement = weekSelectorRef.current.querySelector(`[data-week-key="${weekToSelect.key}"]`) as HTMLElement;
                        if (weekElement && weekSelectorRef.current) {
                            const containerWidth = weekSelectorRef.current.offsetWidth;
                            const elementLeft = weekElement.offsetLeft;
                            const elementWidth = weekElement.offsetWidth;
                            const scrollPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);

                            weekSelectorRef.current.scrollTo({
                                left: scrollPosition,
                                behavior: 'smooth'
                            });
                        }
                    }
                }, 300);
            }).catch((error: any) => {
                console.error('Error loading planning:', error);
                setLoading(false);
            });
        }

        // Calculate current week key
        const today = new Date();
        const year = today.getFullYear();
        const week = getWeekNumber(today);
        setCurrentWeekKey(`${year}-${week.toString().padStart(2, '0')}`);
    }, [show, seasonId, teamId, weekKey]);

    /**
     * Get ISO week number for a given date
     */
    const getWeekNumber = (date: Date): number => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    };

    /**
     * Handle week selection from horizontal list
     */
    const handleWeekClick = (week: PlanningWeek, event: React.MouseEvent<HTMLDivElement>) => {
        setSelectedWeek(week);

        // Center the selected week in the carousel
        const weekElement = event.currentTarget;
        const containerElement = weekSelectorRef.current;

        if (containerElement && weekElement) {
            const containerWidth = containerElement.offsetWidth;
            const weekLeft = weekElement.offsetLeft;
            const weekWidth = weekElement.offsetWidth;

            const scrollPosition = weekLeft - (containerWidth / 2) + (weekWidth / 2);

            containerElement.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
            });
        }
    };

    /**
     * Scroll the week selector left
     */
    const scrollLeft = () => {
        if (weekSelectorRef.current) {
            weekSelectorRef.current.scrollBy({ left: -300, behavior: 'smooth' });
        }
    };

    /**
     * Scroll the week selector right
     */
    const scrollRight = () => {
        if (weekSelectorRef.current) {
            weekSelectorRef.current.scrollBy({ left: 300, behavior: 'smooth' });
        }
    };

    /**
     * Check if week has any training data
     */
    const weekHasData = (week: PlanningWeek): boolean => {
        return week.days.some((day: PlanningDay) => {
            return day.values.some((value: PlanningDayValue) => {
                return value.value && value.value.trim() !== '';
            });
        });
    };

    /**
     * Export current week to PDF
     */
    const handleExportPDF = async () => {
        if (!selectedWeek) return;

        if (!weekHasData(selectedWeek)) {
            alert(t("No training data available for this week. Cannot generate PDF."));
            return;
        }

        try {
            setExportingPDF(true);

            // Capture chart image
            let chartImage = null;
            if (chartRef.current) {
                try {
                    const echartsInstance = chartRef.current.getEchartsInstance();
                    if (echartsInstance) {
                        chartImage = echartsInstance.getDataURL({
                            type: 'png',
                            pixelRatio: 2,
                            backgroundColor: '#fff'
                        });
                    }
                } catch (chartError) {
                    console.error('Error capturing chart:', chartError);
                }
            }

            const response = await exportWeeklyPlanningPDF(
                seasonId,
                teamId,
                selectedWeek.key,
                chartImage,
                selectedWeek.name
            );

            if (response.data.success) {
                const binaryString = atob(response.data.content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.download = response.data.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                alert(t("Error generating PDF. Try again later."));
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            alert(t("Error generating PDF. Try again later."));
        } finally {
            setExportingPDF(false);
        }
    };

    /**
     * Format date range for week display
     */
    const formatWeekRange = (week: PlanningWeek): string => {
        const fromDate = new Date(week.from);
        const toDate = new Date(week.to);

        const fromDay = fromDate.getDate();
        const fromMonth = fromDate.toLocaleDateString('es-ES', { month: 'short' });
        const fromYear = fromDate.getFullYear();
        const toDay = toDate.getDate();
        const toMonth = toDate.toLocaleDateString('es-ES', { month: 'short' });
        const toYear = toDate.getFullYear();

        if (fromMonth === toMonth && fromYear === toYear) {
            return `${fromDay}-${toDay} ${fromMonth} ${fromYear}`;
        } else if (fromYear === toYear) {
            return `${fromDay} ${fromMonth} - ${toDay} ${toMonth} ${fromYear}`;
        } else {
            return `${fromDay} ${fromMonth} ${fromYear} - ${toDay} ${toMonth} ${toYear}`;
        }
    };

    /**
     * Get training sessions for a specific day and moment
     */
    const getDayMomentSessions = (day: PlanningDay, moment: string) => {
        const sessions = day.values.filter((v: PlanningDayValue) =>
            v.day_moment === moment && v.field === 'type' && v.value && v.value.trim() !== ''
        );
        return sessions;
    };

    /**
     * Get session value for a specific field
     */
    const getSessionValue = (day: PlanningDay, moment: string, session: string, field: string): string => {
        const value = day.values.find((v: PlanningDayValue) =>
            v.day_moment === moment && v.day_moment_session === session && v.field === field
        );
        return value ? value.value : '';
    };

    /**
     * Get color for a training session
     */
    const getSessionColor = (day: PlanningDay, moment: string, session: string): string => {
        const colorValue = getSessionValue(day, moment, session, 'color');
        if (colorValue) {
            return colorValue;
        }
        return moment === 'afternoon' ? '#F92C84' : '#009FE3';
    };

    /**
     * Calculate daily totals for chart
     */
    const calculateDailyTotals = (week: PlanningWeek) => {
        const dailyData = week.days.map((day: PlanningDay) => {
            let totalKM = 0;
            let totalTH = 0;
            let totalGroundHours = 0;

            day.values.forEach((value: PlanningDayValue) => {
                if (value.field === 'KM' && value.value) {
                    totalKM += parseSpanishFloat(value.value);
                }

                if (value.field === 'TH' && value.value) {
                    const timeValue = value.value;
                    if (timeValue.includes(':')) {
                        const [hours, minutes] = timeValue.split(':').map(Number);
                        totalTH += hours + (minutes / 60);
                    } else {
                        totalTH += parseSpanishFloat(timeValue);
                    }
                }

                const groundLevels = planningLevels.filter(l => l.type === 'ground');
                groundLevels.forEach(level => {
                    if (value.field === level.key && value.value) {
                        totalGroundHours += parseSpanishFloat(value.value);
                    }
                });
            });

            const dayDate = new Date(day.key);
            const dayName = dayDate.toLocaleDateString('es-ES', { weekday: 'short' });
            const dayNumber = dayDate.getDate();

            return {
                label: `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dayNumber}`,
                km: totalKM,
                th: totalTH,
                groundHours: totalGroundHours
            };
        });

        return dailyData;
    };

    /**
     * Get chart options for weekly training data
     */
    const getChartOptions = (week: PlanningWeek) => {
        const dailyData = calculateDailyTotals(week);

        return {
            title: {
                text: t('Weekly Training Distribution'),
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    fontWeight: 600
                }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                }
            },
            legend: {
                data: [t('WATER (km)'), t('TOTAL (hours)'), t('DRY (hours)')],
                top: 30
            },
            grid: {
                left: '60px',
                right: '80px',
                bottom: '3%',
                containLabel: false
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dailyData.map(d => d.label)
            },
            yAxis: [
                {
                    type: 'value',
                    name: t('Km/day'),
                    position: 'left',
                    nameTextStyle: {
                        padding: [0, 0, 0, 30]
                    }
                },
                {
                    type: 'value',
                    name: t('Hours/day'),
                    position: 'right',
                    nameTextStyle: {
                        padding: [0, 30, 0, 0]
                    }
                }
            ],
            series: [
                {
                    name: t('WATER (km)'),
                    type: 'line',
                    yAxisIndex: 0,
                    data: dailyData.map(d => d.km.toFixed(1)),
                    smooth: true,
                    lineStyle: {
                        width: 3,
                        color: '#009FE3'
                    },
                    itemStyle: {
                        color: '#009FE3'
                    }
                },
                {
                    name: t('TOTAL (hours)'),
                    type: 'line',
                    yAxisIndex: 1,
                    data: dailyData.map(d => d.th.toFixed(1)),
                    smooth: true,
                    lineStyle: {
                        width: 3,
                        color: '#34B689'
                    },
                    itemStyle: {
                        color: '#34B689'
                    }
                },
                {
                    name: t('DRY (hours)'),
                    type: 'line',
                    yAxisIndex: 1,
                    data: dailyData.map(d => d.groundHours.toFixed(1)),
                    smooth: true,
                    lineStyle: {
                        width: 3,
                        color: '#FABF48'
                    },
                    itemStyle: {
                        color: '#FABF48'
                    }
                }
            ]
        };
    };

    /**
     * Get content value for a specific field in the selected week
     */
    const getContentValue = (week: PlanningWeek, fieldKey: string): PlanningWeekContent | null => {
        if (!week.content) return null;
        return week.content.find(c => c.field === fieldKey) || null;
    };

    /**
     * Handle click on C.D. cell to start inline editing
     */
    const handleContentCellClick = (
        event: React.MouseEvent<HTMLTableCellElement>,
        level: { key: string; name: string }
    ) => {
        // Don't allow editing for athletes
        if (isAthlete) return;

        const existingContent = selectedWeek ? getContentValue(selectedWeek, level.key) : null;

        // Calculate position based on the clicked cell
        const rect = event.currentTarget.getBoundingClientRect();
        setPopoverPosition({
            top: rect.bottom + 5,
            left: rect.left
        });

        setEditingContent({
            fieldKey: level.key,
            fieldName: level.name,
            value: existingContent?.value || '',
            color: existingContent?.color || '#ffffff',
            observations: existingContent?.observations || ''
        });
    };

    /**
     * Handle closing the inline editor
     */
    const handleCloseInlineEditor = () => {
        setEditingContent(null);
        setPopoverPosition(null);
    };

    /**
     * Handle saving content from inline editor
     */
    const handleSaveInlineContent = async () => {
        if (!selectedWeek || !selectedWeek.id || !editingContent) return;

        const existingContent = getContentValue(selectedWeek, editingContent.fieldKey);

        try {
            setSavingContent(true);
            let newContentId = 0;

            if (existingContent && existingContent.id) {
                // Update existing content
                const body = {
                    id: existingContent.id,
                    planning_week_id: selectedWeek.id,
                    field: editingContent.fieldKey,
                    value: editingContent.value,
                    color: editingContent.color,
                    observations: editingContent.observations
                };
                await updatePlanningWeekContent(body);
                newContentId = existingContent.id;
            } else {
                // Create new content
                const body = {
                    planning_week_id: selectedWeek.id,
                    field: editingContent.fieldKey,
                    value: editingContent.value,
                    color: editingContent.color,
                    observations: editingContent.observations
                };
                const response = await createPlanningWeekContent(body);
                if (response.data && response.data.id) {
                    newContentId = response.data.id;
                }
            }

            // Update local state
            const updatedPlanning = { ...planning };
            updatedPlanning.months = updatedPlanning.months.map((month: PlanningMonth) => ({
                ...month,
                weeks: month.weeks.map((w: PlanningWeek) => {
                    if (w.key !== selectedWeek.key) return w;

                    const updatedContent = [...(w.content || [])];
                    const contentIndex = updatedContent.findIndex(c => c.field === editingContent.fieldKey);

                    if (contentIndex >= 0) {
                        updatedContent[contentIndex] = {
                            ...updatedContent[contentIndex],
                            value: editingContent.value,
                            color: editingContent.color,
                            observations: editingContent.observations
                        };
                    } else {
                        updatedContent.push({
                            id: newContentId,
                            planning_week_id: selectedWeek.id!,
                            field: editingContent.fieldKey,
                            value: editingContent.value,
                            color: editingContent.color,
                            observations: editingContent.observations
                        });
                    }

                    return { ...w, content: updatedContent };
                })
            }));

            setPlanning(updatedPlanning);

            // Also update selectedWeek to reflect the change immediately
            const updatedWeek = updatedPlanning.months
                .flatMap(m => m.weeks)
                .find(w => w.key === selectedWeek.key);
            if (updatedWeek) {
                setSelectedWeek(updatedWeek);
            }

            handleCloseInlineEditor();
        } catch (error) {
            console.error('Error saving content:', error);
            alert(t("Error saving content. Please try again."));
        } finally {
            setSavingContent(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="xl" className="weekly-planning-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    {t("Weekly Planning View")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="planning-view-container">
                {loading && <LoadingSpinner />}

                {!loading && !selectedWeek && (
                    <div className="alert alert-warning">
                        {t("Week not found")}
                    </div>
                )}

                {!loading && planning.months.length > 0 && selectedWeek && (
                    <>
                        {/* Horizontal week selector with carousel navigation */}
                        <div className="week-selector-wrapper">
                            <button className="week-nav-button left" onClick={scrollLeft} aria-label={t("Previous weeks")}>
                                <i className="bi bi-chevron-left"></i>
                            </button>
                            <div className="week-selector" ref={weekSelectorRef}>
                                {planning.months.map((month: PlanningMonth) =>
                                    month.weeks.map((week: PlanningWeek) => (
                                        <div
                                            key={week.key}
                                            data-week-key={week.key}
                                            className={`week-item ${selectedWeek?.key === week.key ? 'selected' : ''} ${week.key === currentWeekKey ? 'current' : ''}`}
                                            onClick={(event) => handleWeekClick(week, event)}
                                        >
                                            <div className="week-number">{week.name}</div>
                                            <div className="week-range">{formatWeekRange(week)}</div>
                                        </div>
                                    ))
                                )}
                            </div>
                            <button className="week-nav-button right" onClick={scrollRight} aria-label={t("Next weeks")}>
                                <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>

                        {/* Weekly view table */}
                        {selectedWeek && (
                            <div className="weekly-view">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h3 className="mb-0">{t("Microcycle")} {selectedWeek.name} - {formatWeekRange(selectedWeek)}</h3>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleExportPDF}
                                        disabled={exportingPDF}
                                    >
                                        {exportingPDF ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {t("Generating...")}
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-file-pdf me-2"></i>
                                                {t("Export PDF")}
                                            </>
                                        )}
                                    </Button>
                                </div>

                                <Table bordered className="weekly-view-table">
                                    <thead>
                                        <tr>
                                            {selectedWeek.days.map((day: PlanningDay) => {
                                                const dayDate = new Date(day.key);
                                                const dayNumber = dayDate.getDate();
                                                const dayName = dayDate.toLocaleDateString('es-ES', { weekday: 'long' });
                                                const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
                                                return (
                                                    <th key={day.key} className="day-header">
                                                        {capitalizedDayName} {dayNumber}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const maxSessions = Math.max(...selectedWeek.days.map((day: PlanningDay) => {
                                                const morningSessions = getDayMomentSessions(day, 'morning');
                                                const afternoonSessions = getDayMomentSessions(day, 'afternoon');
                                                return morningSessions.length + afternoonSessions.length;
                                            }));

                                            return Array.from({ length: maxSessions }, (_, sessionIndex) => (
                                                <tr key={sessionIndex}>
                                                    {selectedWeek.days.map((day: PlanningDay) => {
                                                        const morningSessions = getDayMomentSessions(day, 'morning');
                                                        const afternoonSessions = getDayMomentSessions(day, 'afternoon');
                                                        const allSessions = [...morningSessions, ...afternoonSessions];
                                                        const session = allSessions[sessionIndex];

                                                        if (!session) {
                                                            return <td key={day.key} className="day-cell empty"></td>;
                                                        }

                                                        const moment = morningSessions.includes(session) ? 'morning' : 'afternoon';
                                                        const description = getSessionValue(day, moment, session.day_moment_session, 'description');
                                                        const pct = getSessionValue(day, moment, session.day_moment_session, 'pct');
                                                        const type = session.value;
                                                        const sessionColor = getSessionColor(day, moment, session.day_moment_session);

                                                        const sessionUrl = isAthlete
                                                            ? `#/athlete/planning/session/${seasonId}/${teamId}/${day.key}/${moment}/${session.day_moment_session}`
                                                            : `#/coach/planning/session/${seasonId}/${teamId}/${day.key}/${moment}/${session.day_moment_session}`;

                                                        return (
                                                            <td key={day.key} className="day-cell">
                                                                <div className="training-session" style={{ borderLeftColor: sessionColor }}>
                                                                    <div className="session-header">
                                                                        <span className="session-type">{type}</span>
                                                                        {pct && <span className="session-pct">({pct}%)</span>}
                                                                        <a
                                                                            href={sessionUrl}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="session-details-link"
                                                                            title={t("View details")}
                                                                        >
                                                                            <i className="bi bi-box-arrow-up-right text-primary"></i>
                                                                        </a>
                                                                    </div>
                                                                    {description && <div className="session-description">{description}</div>}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ));
                                        })()}
                                    </tbody>
                                </Table>

                                {/* Summary table with training type totals */}
                                <div className="weekly-summary">
                                    <h4>{t("Weekly Summary")}</h4>
                                    <Table bordered className="summary-table">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                {planningLevels.map((level: any) => (
                                                    <th key={level.key}>{level.name}</th>
                                                ))}
                                                <th>KM</th>
                                                <th>TH</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="row-label">{t("N. Sess.")}</td>
                                                {planningLevels.map((level: any) => {
                                                    let totalSessions = 0;
                                                    selectedWeek.days.forEach((day: PlanningDay) => {
                                                        day.values.forEach((value: PlanningDayValue) => {
                                                            if (value.field === level.key && value.value) {
                                                                const parsedValue = parseSpanishFloat(value.value);
                                                                if (parsedValue > 0) {
                                                                    totalSessions += parsedValue;
                                                                }
                                                            }
                                                        });
                                                    });
                                                    return (
                                                        <td key={level.key}>
                                                            {totalSessions > 0 ? Number(totalSessions.toFixed(1)).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                                                        </td>
                                                    );
                                                })}
                                                <td>
                                                    {(() => {
                                                        let totalKM = 0;
                                                        selectedWeek.days.forEach((day: PlanningDay) => {
                                                            day.values.forEach((value: PlanningDayValue) => {
                                                                if (value.field === 'KM' && value.value) {
                                                                    totalKM += parseSpanishFloat(value.value);
                                                                }
                                                            });
                                                        });
                                                        return totalKM > 0 ? Number(totalKM.toFixed(1)).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-';
                                                    })()}
                                                </td>
                                                <td>
                                                    {(() => {
                                                        let totalTH = 0;
                                                        selectedWeek.days.forEach((day: PlanningDay) => {
                                                            day.values.forEach((value: PlanningDayValue) => {
                                                                if (value.field === 'TH' && value.value) {
                                                                    const timeValue = value.value;
                                                                    if (timeValue.includes(':')) {
                                                                        const [hours, minutes] = timeValue.split(':').map(Number);
                                                                        totalTH += hours + (minutes / 60);
                                                                    } else {
                                                                        totalTH += parseSpanishFloat(timeValue);
                                                                    }
                                                                }
                                                            });
                                                        });
                                                        if (totalTH > 0) {
                                                            const hours = Math.floor(totalTH);
                                                            const minutes = Math.round((totalTH - hours) * 60);
                                                            return `${hours}:${minutes.toString().padStart(2, '0')}`;
                                                        }
                                                        return '-';
                                                    })()}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="row-label">{t("C.D.")}</td>
                                                {planningLevels.map((level: any) => {
                                                    const contentValue = selectedWeek.content?.find(c => c.field === level.key);
                                                    const value = contentValue?.value || '';
                                                    const backgroundColor = contentValue?.color || '#ffffff';
                                                    const hasContent = contentValue && (contentValue.value || contentValue.observations);
                                                    const isWhiteBackground = backgroundColor.toLowerCase() === '#ffffff' || backgroundColor.toLowerCase() === '#fff' || backgroundColor.toLowerCase() === 'white';
                                                    const textColor = isWhiteBackground ? '#06a099' : '#fff';
                                                    const isEditing = editingContent?.fieldKey === level.key;
                                                    return (
                                                        <td
                                                            key={level.key}
                                                            className={`content-distribution-cell ${isAthlete ? 'read-only' : 'editable'}`}
                                                            style={{
                                                                backgroundColor: backgroundColor,
                                                                color: textColor,
                                                                cursor: isAthlete ? 'default' : 'pointer',
                                                                position: 'relative'
                                                            }}
                                                            onClick={(e) => handleContentCellClick(e, level)}
                                                        >
                                                            {value || ''}
                                                            {hasContent && !isEditing && (
                                                                <div className="content-tooltip">
                                                                    {contentValue.value && (
                                                                        <div className="content-tooltip-value">
                                                                            {t("Value")}: {contentValue.value}
                                                                        </div>
                                                                    )}
                                                                    {contentValue.observations && (
                                                                        <div className="content-tooltip-observations">
                                                                            {contentValue.observations}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td>-</td>
                                                <td>-</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Weekly training chart */}
                                <div className="weekly-chart">
                                    <ReactECharts
                                        ref={chartRef}
                                        option={getChartOptions(selectedWeek)}
                                        style={{ height: '400px', width: '100%' }}
                                        opts={{ renderer: 'canvas' }}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    {t("Close")}
                </Button>
            </Modal.Footer>

            {/* Inline Content Distribution Editor */}
            {editingContent && (
                <>
                    {/* Backdrop to close on click outside */}
                    <div
                        className="content-edit-backdrop"
                        onClick={handleCloseInlineEditor}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 10099
                        }}
                    />
                    <div className="content-edit-popover" style={{
                        position: 'fixed',
                        top: popoverPosition?.top ?? '50%',
                        left: popoverPosition?.left ?? '50%',
                        zIndex: 10100,
                        minWidth: '280px',
                        maxWidth: '320px',
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                        border: '1px solid #dee2e6'
                    }}>
                        <div className="popover-header d-flex justify-content-between align-items-center"
                            style={{
                                backgroundColor: '#06a099',
                                color: '#fff',
                                padding: '8px 12px',
                                borderTopLeftRadius: '5px',
                                borderTopRightRadius: '5px',
                                fontSize: '14px',
                                fontWeight: 500
                            }}>
                            <span>{t("Edit")} {editingContent.fieldName}</span>
                            <button
                                type="button"
                                className="btn-close btn-close-white btn-close-sm"
                                aria-label={t("Close")}
                                onClick={handleCloseInlineEditor}
                                style={{ fontSize: '10px' }}
                            ></button>
                        </div>
                        <div className="popover-body" style={{ padding: '12px' }}>
                            <Form onSubmit={(e) => { e.preventDefault(); handleSaveInlineContent(); }}>
                                <div className="row mb-2">
                                    <div className="col-7">
                                        <Form.Group controlId="inlineContentValue">
                                            <Form.Label className="small mb-1">{t("Value")}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.1"
                                                size="sm"
                                                value={editingContent.value}
                                                onChange={(e) => setEditingContent(prev =>
                                                    prev ? { ...prev, value: e.target.value } : null
                                                )}
                                                placeholder="0.0"
                                                autoFocus
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-5">
                                        <Form.Group controlId="inlineContentColor">
                                            <Form.Label className="small mb-1">{t("Color")}</Form.Label>
                                            <Form.Control
                                                type="color"
                                                size="sm"
                                                value={editingContent.color}
                                                onChange={(e) => setEditingContent(prev =>
                                                    prev ? { ...prev, color: e.target.value } : null
                                                )}
                                                style={{ width: '100%', height: '31px' }}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>

                                <Form.Group className="mb-2" controlId="inlineContentObservations">
                                    <Form.Label className="small mb-1">{t("Observations")}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        size="sm"
                                        value={editingContent.observations}
                                        onChange={(e) => setEditingContent(prev =>
                                            prev ? { ...prev, observations: e.target.value } : null
                                        )}
                                        placeholder={t("Additional notes")}
                                    />
                                </Form.Group>

                                <div className="d-flex justify-content-end gap-2 mt-2">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        type="button"
                                        onClick={handleCloseInlineEditor}
                                    >
                                        {t("Cancel")}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        type="submit"
                                        disabled={savingContent}
                                    >
                                        {savingContent ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                {t("Saving...")}
                                            </>
                                        ) : (
                                            t("Save")
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
}

export default WeeklyPlanningModal;
